import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Menu,
  X,
  Home,
  Search,
  User,
  Bell,
  Settings,
  LogOut,
  Boxes,
  ChevronRight,
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useToast } from './Toast'

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const { user, logout } = useAuth()
  const toast = useToast()
  // 复购触达 V3：未读通知角标
  const [unread, setUnread] = useState(0)
  useEffect(() => {
    if (!user) return
    const load = () => import('../services/api').then(({ default: api }) =>
      api.get('/notifications/unread-count').then((r: any) => setUnread(r?.unread ?? 0)).catch(() => {}),
    )
    load()
    const t = setInterval(load, 30000)
    return () => clearInterval(t)
  }, [user])
  const location = useLocation()
  const userMenuRef = useRef<HTMLDivElement>(null)

  // 点击外部关闭用户菜单
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = () => {
    logout()
    setIsUserMenuOpen(false)
    toast.info('已退出登录')
  }

  const navItems = [
    { path: '/', icon: Home, label: '首页' },
    { path: '/world', icon: Boxes, label: '世界' },
    { path: '/verify', icon: Search, label: '验真' },
    { path: '/space-square', icon: Boxes, label: '展馆' },
    { path: '/orders', icon: User, label: '订单' },
    { path: '/profile', icon: Settings, label: '我的' },
  ]
  // 移动菜单补充入口（主导航放不下的受保护页面）
  const moreItems = [
    { path: '/rewards', label: '我的权益' },
    { path: '/space/my', label: '我的展馆' },
    { path: '/report', label: '假货举报' },
  ]

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-cyber-dark/95 backdrop-blur-sm border-b border-cyber-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <div className="w-8 h-8 bg-gradient-to-br from-cyber-neon to-cyber-pink rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">P</span>
            </div>
            <span className="ml-2 text-lg font-bold text-white">Phygital</span>
          </Link>

          <nav className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-1.5 transition-colors ${
                    isActive ? 'text-cyber-neon' : 'text-gray-300 hover:text-cyber-neon'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-sm">{item.label}</span>
                </Link>
              )
            })}
          </nav>

          <div className="flex items-center space-x-4">
            {user ? (
              <>
              {/* 复购触达 V3：通知铃铛（30s 轮询未读数） */}
              <Link to="/notifications" className="relative p-2 text-gray-300 hover:text-cyber-neon transition-colors">
                <Bell className="w-5 h-5" />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 rounded-full bg-cyber-pink text-white text-[10px] font-bold flex items-center justify-center">
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </Link>
              
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 px-4 py-2 bg-cyber-dark border border-cyber-neon/30 rounded-lg hover:border-cyber-neon transition-colors"
                >
                  <div className="w-6 h-6 bg-gradient-to-br from-cyber-neon to-cyber-pink rounded-full flex items-center justify-center">
                    <User className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-sm text-gray-300">
                    {user.nickname || user.phone?.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}
                  </span>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-cyber-dark border border-cyber-border rounded-lg shadow-xl z-50">
                    <div className="p-2 space-y-1">
                      <Link
                        to="/orders"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 rounded-lg transition-colors"
                      >
                        <User className="w-4 h-4" />
                        我的订单
                      </Link>
                      <Link
                        to="/profile"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 rounded-lg transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        个人中心
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-cyber-red hover:bg-cyber-red/10 rounded-lg transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        退出登录
                      </button>
                    </div>
                  </div>
                )}
              </div>
              </>
            ) : (
              <Link
                to="/login"
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyber-neon to-cyber-pink rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
              >
                <User className="w-4 h-4" />
                <span>登录</span>
              </Link>
            )}
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-gray-400 hover:text-white"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="md:hidden bg-cyber-dark border-t border-cyber-border">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-gray-300 hover:text-cyber-neon transition-colors"
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            ))}
            {/* 更多入口 */}
            <div className="border-t border-cyber-border my-2" />
            {moreItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:text-cyber-neon hover:bg-cyber-panel transition-colors"
              >
                <span>{item.label}</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  )
}
